// Roteador top-level — escolhe qual painel renderizar conforme o URL prefix.
//
// URLs canônicas (novas):
//   /auth/*    → Auth (login, register, profile picker)
//   /student/* → Aluno
//   /teacher/* → Professor
//   /company/* → Organização
//   /manager/* → Admin de plataforma
//   /pay/*     → Página pública de pagamento
//   "/"        → redireciona pra /auth/
//
// URLs legacy (compat — emails antigos, bookmarks): /auth/, /student/,
// /teacher/, /company/, /manager/ → mesmas telas, mas o painel reescreve
// o histórico pra URL canônica no primeiro pushState. Sem 301 server-side ainda
// (depende do Cloudflare Pages Redirects).

import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { mountSidebarShortcuts } from "@app/ui/lib/theme";
import { setPersistentPlayerEnabled } from "@app/ui";

// Imports diretos (não lazy ainda) — todos os painéis vão no bundle inicial.
// Próximo passo é trocar pra `lazy()` quando o bundle estiver gordo, mas
// hoje a soma é manejável e simplifica o debug.
import {
  App as AuthApp
} from "auth-new";
import {
  App as StudentApp,
  toggleSidebarResponsive as studentToggleSidebar,
  CadernoPopupPage
} from "student-new";
import {
  App as TeacherApp,
  toggleSidebarResponsive as teacherToggleSidebar
} from "teacher-new";
import {
  App as CompanyApp,
  toggleSidebarResponsive as companyToggleSidebar
} from "company-new";
import {
  App as ManagerApp,
  toggleSidebarResponsive as managerToggleSidebar
} from "manager-new";
import { App as PayApp } from "pay-new";

type PanelKind =
  | "auth"
  | "student"
  | "teacher"
  | "company"
  | "manager"
  | "pay"
  | "unknown";

function detectPanel(pathname: string): PanelKind {
  if (pathname === "/" || pathname === "") return "auth";
  // Canonical (sem "new")
  if (pathname.startsWith("/auth")) return "auth";
  if (pathname.startsWith("/student")) return "student";
  if (pathname.startsWith("/teacher")) return "teacher";
  if (pathname.startsWith("/company")) return "company";
  if (pathname.startsWith("/manager")) return "manager";
  if (pathname.startsWith("/pay")) return "pay";
  // Legacy (compat — bookmarks/emails antigos)
  if (pathname.startsWith("/auth")) return "auth";
  if (pathname.startsWith("/student")) return "student";
  if (pathname.startsWith("/teacher")) return "teacher";
  if (pathname.startsWith("/company")) return "company";
  if (pathname.startsWith("/manager")) return "manager";
  return "unknown";
}

/** Reescreve o URL legacy /newX/... pra canônico /X/... sem reload nem 301.
 *  Roda no boot — se o user chegou via link velho, a barra atualiza pra
 *  versão limpa imediatamente. Mantém path/search/hash. */
function rewriteLegacyUrl() {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  const legacyMap: Record<string, string> = {
    "/auth": "/auth",
    "/student": "/student",
    "/teacher": "/teacher",
    "/company": "/company",
    "/manager": "/manager"
  };
  for (const [from, to] of Object.entries(legacyMap)) {
    if (path === from || path.startsWith(from + "/")) {
      const newPath = to + path.slice(from.length);
      window.history.replaceState(
        window.history.state,
        "",
        newPath + window.location.search + window.location.hash
      );
      return;
    }
  }
}
rewriteLegacyUrl();

const currentPanel = signal<PanelKind>(
  typeof window !== "undefined"
    ? detectPanel(window.location.pathname)
    : "auth"
);

// Pay-new força tema claro. Os outros painéis seguem o tema do usuário.
function applyPanelTheme(panel: PanelKind) {
  if (panel === "pay") {
    document.documentElement.classList.remove("dark");
    document.documentElement.classList.add("light");
  }
}

export function Shell() {
  const panel = currentPanel.value;

  useEffect(() => {
    function onPopState() {
      const next = detectPanel(window.location.pathname);
      if (next !== currentPanel.value) {
        applyPanelTheme(next);
        currentPanel.value = next;
      }
    }
    // Patch history.pushState pra disparar popstate sintético — assim
    // navegação interna dos painéis (que chama pushState) também atualiza
    // o Shell quando atravessa prefix.
    const origPush = window.history.pushState.bind(window.history);
    window.history.pushState = function (state, title, url) {
      origPush(state, title, url);
      window.dispatchEvent(new PopStateEvent("popstate"));
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.history.pushState = origPush;
    };
  }, []);

  // Sidebar shortcuts (Ctrl+/ Ctrl+\ Ctrl+B) — só monta pro painel ativo
  // que tem sidebar. Auth e Pay não têm.
  useEffect(() => {
    let cleanup: (() => void) | null = null;
    if (panel === "student") cleanup = mountSidebarShortcuts(studentToggleSidebar);
    else if (panel === "teacher") cleanup = mountSidebarShortcuts(teacherToggleSidebar);
    else if (panel === "company") cleanup = mountSidebarShortcuts(companyToggleSidebar);
    else if (panel === "manager") cleanup = mountSidebarShortcuts(managerToggleSidebar);
    applyPanelTheme(panel);
    // PersistentPlayer não aparece em:
    //   - login/pay (sem contexto de usuário, atrapalha)
    //   - pop-ups de anotação (janela dedicada e focada — player no rodapé
    //     polui a janela e ainda dá pra controlar pela aba principal).
    //     [[project-notes-system-caderno]]
    //   - pop-up do Gravador de Aulas (música de fundo entra no system-audio
    //     capture e o player rouba foco visual numa janela de silêncio).
    //     [[project-recorder-audio-lectures]]
    let inIsolatedPopup = false;
    try {
      const view = new URL(window.location.href).searchParams.get("view");
      inIsolatedPopup =
        view === "notes-window" ||
        view === "note-window" ||
        view === "recorder-window" ||
        view === "quickstudy-window";
    } catch { /* ignore */ }
    // IMPORTANTE: o pop-up isolado NÃO renderiza o player (ver main.tsx) e NÃO
    // pode mexer no flag PERSISTENTE `enabled`. Esse flag vive em localStorage e
    // é sincronizado entre janelas por evento `storage` — então chamar
    // setPersistentPlayerEnabled(false) aqui escrevia "0" no storage e DERRUBAVA
    // o player da janela principal (e zerava a preferência on/off do usuário).
    // Bug do pop-out do Tira-Dúvida. [[project-persistent-music-player]]
    if (!inIsolatedPopup) {
      setPersistentPlayerEnabled(panel !== "auth" && panel !== "pay");
    }
    return () => {
      if (cleanup) cleanup();
    };
  }, [panel]);

  if (panel === "auth") return <AuthApp />;
  // Rota dedicada de popup do caderno de redação (window.open).
  // Renderiza apenas o painel, sem nav/sidebar do StudentApp.
  // [[project-caderno-realista]]
  if (
    panel === "student" &&
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/student/caderno-popup/")
  ) {
    return <CadernoPopupPage />;
  }
  if (panel === "student") return <StudentApp />;
  if (panel === "teacher") return <TeacherApp />;
  if (panel === "company") return <CompanyApp />;
  if (panel === "manager") return <ManagerApp />;
  if (panel === "pay") return <PayApp />;

  // unknown — redireciona pra auth (canônico)
  if (typeof window !== "undefined" && window.location.pathname !== "/auth/") {
    window.history.replaceState({}, "", "/auth/");
    currentPanel.value = "auth";
  }
  return <AuthApp />;
}
